// Browser hand-off to IBM Cloud for watsonx single sign-on.
//
// The api returns either { mode: 'post', acsUrl, SAMLResponse, RelayState } (a
// signed SAML assertion that must reach IBM as an HTML form POST - the SAML
// HTTP-POST binding) or { mode: 'redirect', url }.

import axios from 'axios';
import { API_BASE_URL } from '@config';

export const PENDING_SSO_KEY = 'heidi_watsonx_pending_sso';

// Every hand-off to IBM Cloud opens in this one named tab, so going back and
// forth between Heidi and IBM is switching between two tabs, not a growing pile.
export const WATSONX_TAB = 'heidi-watsonx';

// Outlined buttons sit on the dark page header, where the theme's default
// outlined colour (primary blue) is close to invisible. Use light text instead.
export const HEADER_BUTTON_STYLE = { color: '#fff', borderColor: 'rgba(255, 255, 255, 0.6)' };

// IBM-initiated sign-in lands on /watsonx/sso/<request>. When the user is not
// signed in to Heidi yet, the router bounces to /login and drops the path, so
// remember the request here - this module loads with the toolbar, before any
// route renders - and let the toolbar resume it after login.
if (typeof window !== 'undefined') {
	const match = window.location.pathname.match(/^\/watsonx\/sso\/([A-Za-z0-9]{16,128})\/?$/);
	if (match) {
		try {
			sessionStorage.setItem(PENDING_SSO_KEY, match[1]);
		} catch (e) {
			// storage disabled: the page itself still completes when already signed in
		}
	}
}

export function takePendingSsoRequest() {
	try {
		const key = sessionStorage.getItem(PENDING_SSO_KEY);
		if (key) sessionStorage.removeItem(PENDING_SSO_KEY);
		return key;
	} catch (e) {
		return null;
	}
}

/**
 * Send `win` (a window we control) to IBM with the api's hand-off.
 */
export function submitHandoff(win, handoff) {
	if (!handoff) throw new Error('No hand-off from the server');
	if (handoff.mode === 'redirect') {
		win.location.href = handoff.url;
		return;
	}
	const doc = win.document;
	const form = doc.createElement('form');
	form.method = 'POST';
	form.action = handoff.acsUrl;
	const fields = { SAMLResponse: handoff.SAMLResponse, RelayState: handoff.RelayState || '' };
	Object.keys(fields).forEach((name) => {
		if (name === 'RelayState' && !fields[name]) return;
		const input = doc.createElement('input');
		input.type = 'hidden';
		input.name = name;
		input.value = fields[name];
		form.appendChild(input);
	});
	doc.body.appendChild(form);
	form.submit();
}

// IBM's SP login URL always lands on the IBM Cloud dashboard: it ignores any
// target we pass it. So "Open in IBM Cloud" watches the watsonx tab from the
// Heidi page that opened it and, once the sign-in is done, sends that tab on to
// the product. While the tab is on a Heidi page its location is readable; on
// IBM it is not (cross-origin), which is all we need to follow the hops:
// /watsonx/open -> IBM login -> /watsonx/sso/<request> -> IBM ACS -> dashboard.
const WATCH_EVERY_MS = 500;
// After /watsonx/sso posted the assertion: time for IBM to set its session
// (a first-time user also goes through IBM's short account-setup page).
const POSTED_SETTLE_MS = 6000;
// Left for IBM and never came back to /watsonx/sso: the user already had an IBM
// session, so IBM went straight to the dashboard.
const ALREADY_SIGNED_IN_MS = 8000;
const GIVE_UP_MS = 120000;

function followToProduct(win, link) {
	const started = Date.now();
	let sawSso = false;
	let onIbmSince = null;
	const tick = () => {
		if (win.closed || Date.now() - started > GIVE_UP_MS) return;
		let path;
		try {
			path = win.location.pathname;
		} catch (e) {
			path = null; // on IBM
		}
		if (path === null) {
			if (!onIbmSince) onIbmSince = Date.now();
			const waited = Date.now() - onIbmSince;
			if (waited >= (sawSso ? POSTED_SETTLE_MS : ALREADY_SIGNED_IN_MS)) {
				try {
					win.location.href = link;
				} catch (e) {
					// the tab went somewhere we may not steer; leave it there
				}
				return;
			}
		} else {
			onIbmSince = null;
			if (path.indexOf('/watsonx/sso/') === 0) sawSso = true;
		}
		setTimeout(tick, WATCH_EVERY_MS);
	};
	setTimeout(tick, WATCH_EVERY_MS);
}

/**
 * onClick for the "Open in IBM Cloud" buttons (an <a href="/watsonx/open/<target>">).
 * Opens the watsonx tab from this click, then, with SP-initiated sign-in, follows
 * it to the product page instead of leaving the user on the IBM Cloud dashboard.
 * A blocked pop-up falls back to the link's own navigation.
 */
export function openInIbm(event, target) {
	const win = window.open(`/watsonx/open/${target}`, WATSONX_TAB);
	if (!win) return;
	event.preventDefault();
	axios.get(`${API_BASE_URL}/api/watsonx/menu`)
		.then((res) => {
			const menu = res.data || {};
			if (menu.ssoMode !== 'sp') return; // 'none' opens the product directly
			const item = (menu.items || []).find((i) => i.key === target);
			if (item && item.link) followToProduct(win, item.link);
		})
		.catch(() => {
			// the tab still signs in; it just stays on the IBM Cloud dashboard
		});
}

/**
 * The api's error body and status. Heidi's axios interceptor (jwtService) rejects
 * with the RESPONSE itself, so the body is at err.data; a network error or an
 * interceptor-free call still carries it at err.response.data.
 */
export function apiError(err) {
	const data = (err && (err.data || (err.response && err.response.data))) || {};
	const status = (err && (err.status || (err.response && err.response.status))) || null;
	const message = typeof data === 'string'
		? data
		: (data.message || (data.errors && data.errors[0] && data.errors[0].msg) || null);
	return { status, message };
}
